import { Popover } from "@headlessui/react";
import { useState } from "react";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show,
  TextArea
} from "shared/components";
import { DotsHorizontal, Pencil02 } from "untitledui-js-base";
import {
  ActivityItem,
  ApplicationActivity,
  ApplicationNote,
  DeleteNoteMutation,
  EditNoteMutation
} from "./types";

import moment from "moment";
import { applicationDecisionStatus } from "shared/app/ActivityLog/constants";
import { ApplicantType } from "shared/types";
import { ApplicantRoleLabel, formatToCurrency } from "shared/utils";
import { RoleTitles } from "views/settings-users/constants";

const buttonClassName =
  "w-full flex px-4 py-2.5 !text-base !text-content-1000 !font-medium hover:bg-alpha-40";

const combinedRoles = {
  ...RoleTitles,
  ...ApplicantRoleLabel
};

export const ActivityNote = ({
  deleteNoteMeta,
  editNoteMeta,
  activityItem
}: {
  activityItem: ActivityItem;
  deleteNoteMeta: DeleteNoteMutation;
  editNoteMeta: EditNoteMutation;
}) => {
  const [editMode, setEditMode] = useState<boolean>(false);
  const [newMessage, setNewMessage] = useState<string>(
    typeof activityItem.details === "string" ? activityItem.details : ""
  );

  return (
    <div className="animate-appear w-full ">
      <div className="border rounded-lg border-alpha-100 p-6 w-full bg-white">
        <div className="flex justify-between relative mb-2">
          <Show
            if={
              activityItem.activity_type === "canadian_criminal_report_invited"
            }
          >
            <p className="text-base font-bold text-content-1000">
              Canadian criminal background check email was sent
            </p>
          </Show>

          <Show if={activityItem.activity_type === "note"}>
            <p className="text-base font-bold text-content-1000">
              {activityItem.author?.name}{" "}
              <span className="font-normal">left a note</span>
            </p>
          </Show>

          <Show if={activityItem.activity_type === "invited"}>
            <p className="text-base font-bold text-content-1000">
              {(activityItem as ApplicationActivity).details.email}{" "}
              <span className="font-normal">
                invited as{" "}
                {(activityItem as ApplicationActivity).details.invitee_role ===
                ApplicantType.CoSigner
                  ? "a "
                  : "an "}
                {activityItem.details &&
                typeof activityItem.details === "object"
                  ? ApplicantRoleLabel[
                      (activityItem as ApplicationActivity).details.invitee_role
                    ]
                  : "-"}
              </span>
            </p>
          </Show>

          <Show if={activityItem.activity_type === "group_submitted"}>
            <p className="text-base font-bold text-content-1000">
              Application is pending a decision
            </p>
          </Show>

          <Show if={activityItem.activity_type === "decision_email_sent"}>
            <p className="text-base font-bold text-content-1000">
              Decision email was sent
            </p>
          </Show>

          <Show if={activityItem.activity_type === "credit_report_sent"}>
            <p className="text-base font-bold text-content-1000">
              Credit report was automatically emailed
            </p>
          </Show>

          <Show if={activityItem.activity_type === "submitted"}>
            <p className="text-base font-bold text-content-1000">
              {activityItem.author?.name} ({activityItem.author?.email}){" "}
              <span className="font-normal">submitted their application</span>
            </p>
          </Show>

          <Show if={activityItem.activity_type === "decision_made"}>
            <p className="text-base font-bold text-content-1000">
              {
                applicationDecisionStatus[
                  (activityItem as ApplicationActivity).details.status
                ]
              }
            </p>
          </Show>

          <Show if={activityItem.activity_type === "auto_approved"}>
            <p className="text-base font-bold text-content-1000">
              Application automatically approved
            </p>
          </Show>

          <Show if={activityItem.activity_type === "note"}>
            <Popover className="absolute -right-4 -top-2">
              <Popover.Button>
                <IconButton
                  variant={IconButtonVariant.BASE}
                  size={IconButtonSize.SM}
                  borderLess
                  className="bg-transparent hover:bg-alpha-40"
                  Icon={DotsHorizontal}
                />
              </Popover.Button>
              <Popover.Panel className="absolute right-0 z-10 mt-2 w-[140px] rounded-lg border border-alpha-100 bg-background-1000 shadow-xl">
                <Popover.Button className="w-full">
                  <Button
                    size={ButtonSize.SM}
                    variant={ButtonVariant.TEXT}
                    onClick={() => setEditMode(true)}
                    className={buttonClassName}
                  >
                    Edit note
                  </Button>
                </Popover.Button>
                <Popover.Button className="w-full">
                  <Button
                    size={ButtonSize.SM}
                    variant={ButtonVariant.TEXT}
                    onClick={() =>
                      deleteNoteMeta.mutate(
                        Number((activityItem as ApplicationNote).note_id)
                      )
                    }
                    className={`${buttonClassName} border-t border-alpha-40 !text-danger-500`}
                  >
                    Delete note
                  </Button>
                </Popover.Button>
              </Popover.Panel>
            </Popover>
          </Show>
        </div>

        <Show if={typeof activityItem.details === "string"}>
          {editMode && activityItem.activity_type === "note" ? (
            <>
              <TextArea
                id="note"
                placeholder="You can write a note here"
                value={newMessage}
                rows={4}
                onChange={(e) => setNewMessage(e.target.value)}
              />
              <div className="mt-2 flex justify-end gap-2">
                <Button
                  variant={ButtonVariant.BASE}
                  size={ButtonSize.SM}
                  onClick={() => setEditMode(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant={ButtonVariant.PRIMARY}
                  size={ButtonSize.SM}
                  onClick={() => {
                    editNoteMeta.mutate({
                      noteId: Number((activityItem as ApplicationNote).note_id),
                      newMessage
                    });
                    setEditMode(false);
                  }}
                >
                  Save
                </Button>
              </div>
            </>
          ) : (
            <div>
              <p className="text-sm text-content-1000">
                {activityItem.details as string}
              </p>

              <Show if={activityItem.updated_at}>
                <div className="flex border-t border-alpha-40 gap-2 items-center mt-4 pt-4">
                  <Pencil02 className="stroke-content-200" size="20" />
                  <p className="text-content-400 font-normal text-sm">
                    Edited at{" "}
                    {moment(activityItem.updated_at).format(
                      "MMM DD, YYYY [at] h:mm A"
                    )}
                  </p>
                </div>
              </Show>
            </div>
          )}
        </Show>

        <Show if={activityItem.activity_type === "auto_approved"}>
          <p className="text-sm font-normal text-content-400">by Boom</p>
        </Show>

        {activityItem.details && (
          <>
            <Show if={activityItem.activity_type === "group_submitted"}>
              <p className="text-sm font-normal text-content-400">
                All applicants have submitted their rental applications
              </p>
            </Show>

            <Show if={activityItem.activity_type === "decision_made"}>
              <p className="text-sm font-normal text-content-400">
                by {activityItem.author?.name || "-"},{" "}
                {activityItem.author?.author_role
                  ? combinedRoles[activityItem.author.author_role]
                  : "-"}
              </p>
            </Show>

            <Show
              if={
                activityItem.activity_type ===
                "canadian_criminal_report_invited"
              }
            >
              <p className="text-sm font-normal text-content-400">
                {activityItem.details &&
                  typeof activityItem.details === "object" &&
                  `${activityItem.details.contact?.first_name} ${activityItem.details.contact?.last_name} (${activityItem.details.contact?.email || ""}) was
                emailed a Canadian criminal background check link
              `}
              </p>
            </Show>

            <Show if={activityItem.activity_type === "decision_email_sent"}>
              <p className="text-sm font-normal text-content-400">
                {activityItem.details &&
                  typeof activityItem.details === "object" &&
                  `${activityItem.details.contact?.first_name} ${activityItem.details.contact?.last_name} (${activityItem.details.contact?.email || ""})`}{" "}
                was emailed a decision letter
              </p>
            </Show>

            <Show if={activityItem.activity_type === "credit_report_sent"}>
              <p className="text-sm font-normal text-content-400">
                {activityItem.details &&
                  typeof activityItem.details === "object" &&
                  `${activityItem.details.contact?.first_name} ${activityItem.details.contact?.last_name} (${activityItem.details.contact?.email || "-"})`}{" "}
                was emailed a credit report
              </p>
            </Show>

            <Show
              if={
                activityItem.activity_type === "submitted" &&
                activityItem.details.application_fee
              }
            >
              <p className="text-sm font-normal text-content-400">
                Application fee paid:{" "}
                {activityItem.details &&
                typeof activityItem.details === "object" &&
                activityItem.details.application_fee
                  ? formatToCurrency(activityItem.details.application_fee)
                  : "-"}
              </p>
            </Show>

            <Show if={activityItem.activity_type === "invited"}>
              by {activityItem.author?.name || "-"},{" "}
              {activityItem.author?.author_role
                ? combinedRoles[activityItem.author.author_role]
                : "-"}
            </Show>
          </>
        )}
      </div>

      <p className="flex justify-end mt-2 text-sm text-content-300 mr-4">
        {moment(activityItem.created_at).format("MMM DD, YYYY [at] h:mm A")}
      </p>
    </div>
  );
};
